import { Task, TaskStatus } from '../types/task';
import { Shipping } from '../types/shipping';

interface Quote {
  tid: string;
  carMake: string;
  carModel: string;
  carYear: string;
  carValue: string;
  carCurrency: string;
  carVin: string;
  carLength?: string;
  carWidth?: string;
  carHeight?: string;
  carVolume?: string;
  carWeight?: string;
  originAddress: string;
  destinationAddress: string;
  outputCurrency: string;
  insuranceType: string;
  importManagement: string;
  buyInsuranceForMe: boolean;
  shippingType: string;
  vehicleRegistration: string;
  destinationCountry: string;
  customsRates: any;
  insuranceRates: any;
  paymentServiceFee: string;
  paymentServiceRate: string;
  adminEdits?: any;
  stage: string;
  contactId: string;
  contactInfo?: any; // This might be populated from contacts collection
  billingInfoDifferent?: boolean;
  billingInfo?: {
    fullName: string;
    email: string;
    phone: string;
    country: string;
  };
  createdAt: string;
  updatedAt: string;
  shippingResult?: any;
}

interface Contact {
  _id?: string;
  fullName: string;
  email: string;
  phone: string;
  preferredTime?: string;
  additionalNotes?: string;
  createdAt: string;
  updatedAt: string;
}

interface CacheMetadata {
  key: string;
  lastUpdated: number;
  expiresAt: number;
  version: string;
  checksum?: string;
}

interface SyncStatus {
  lastSync: number;
  pendingOperations: PendingOperation[];
  conflictResolution: 'server-wins' | 'client-wins' | 'manual';
}

interface PendingOperation {
  id: string;
  type: 'create' | 'update' | 'delete';
  table: string;
  data: any;
  timestamp: number;
  retries: number;
}

const DB_NAME = 'LimaClientDB';
const DB_VERSION = 1;

// Store names
const STORES = {
  TASKS: 'tasks',
  SHIPMENTS: 'shipments', 
  QUOTES: 'quotes',
  CONTACTS: 'contacts',
  METADATA: 'metadata',
  SYNC_STATUS: 'sync_status',
  PENDING_OPERATIONS: 'pending_operations'
} as const;

class IndexedDBService {
  private db: IDBDatabase | null = null;
  private isInitializing = false;
  private initPromise: Promise<void> | null = null;

  async init(): Promise<void> {
    if (this.db) return;
    if (this.isInitializing && this.initPromise) {
      return this.initPromise;
    }

    this.isInitializing = true;
    this.initPromise = this._init();
    await this.initPromise;
    this.isInitializing = false;
  }

  private async _init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        reject(new Error('Failed to open IndexedDB'));
      };

      request.onsuccess = (event) => {
        this.db = (event.target as IDBOpenDBRequest).result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Tasks store
        if (!db.objectStoreNames.contains(STORES.TASKS)) {
          const taskStore = db.createObjectStore(STORES.TASKS, { keyPath: '_id.$oid' });
          taskStore.createIndex('shipmentId', 'shipmentId', { unique: false });
          taskStore.createIndex('status', 'status', { unique: false });
          taskStore.createIndex('assignedTo', 'assignedTo.$oid', { unique: false });
          taskStore.createIndex('stage', 'stage', { unique: false });
          taskStore.createIndex('priority', 'priority', { unique: false });
          taskStore.createIndex('updatedAt', 'updatedAt', { unique: false });
        }

        // Shipments store
        if (!db.objectStoreNames.contains(STORES.SHIPMENTS)) {
          const shipmentStore = db.createObjectStore(STORES.SHIPMENTS, { keyPath: '_id' });
          shipmentStore.createIndex('clientEmail', 'clientEmail', { unique: false });
          shipmentStore.createIndex('status', 'status', { unique: false });
          shipmentStore.createIndex('customSID', 'customSID', { unique: false });
          shipmentStore.createIndex('updatedAt', 'updatedAt', { unique: false });
        }

        // Quotes store
        if (!db.objectStoreNames.contains(STORES.QUOTES)) {
          const quoteStore = db.createObjectStore(STORES.QUOTES, { keyPath: 'tid' });
          quoteStore.createIndex('stage', 'stage', { unique: false });
          quoteStore.createIndex('contactId', 'contactId', { unique: false });
          quoteStore.createIndex('createdAt', 'createdAt', { unique: false });
          quoteStore.createIndex('carVin', 'carVin', { unique: false });
        }

        // Contacts store
        if (!db.objectStoreNames.contains(STORES.CONTACTS)) {
          const contactStore = db.createObjectStore(STORES.CONTACTS, { keyPath: '_id' });
          contactStore.createIndex('email', 'email', { unique: false });
          contactStore.createIndex('fullName', 'fullName', { unique: false });
          contactStore.createIndex('phone', 'phone', { unique: false });
          contactStore.createIndex('createdAt', 'createdAt', { unique: false });
        }

        // Metadata store for cache management
        if (!db.objectStoreNames.contains(STORES.METADATA)) {
          db.createObjectStore(STORES.METADATA, { keyPath: 'key' });
        }

        // Sync status store
        if (!db.objectStoreNames.contains(STORES.SYNC_STATUS)) {
          db.createObjectStore(STORES.SYNC_STATUS, { keyPath: 'id' });
        }

        // Pending operations store for offline support
        if (!db.objectStoreNames.contains(STORES.PENDING_OPERATIONS)) {
          const pendingStore = db.createObjectStore(STORES.PENDING_OPERATIONS, { keyPath: 'id' });
          pendingStore.createIndex('type', 'type', { unique: false });
          pendingStore.createIndex('table', 'table', { unique: false });
          pendingStore.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  // Generic CRUD operations
  async get<T>(store: string, key: string): Promise<T | null> {
    await this.init();
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([store], 'readonly');
      const objectStore = transaction.objectStore(store);
      const request = objectStore.get(key);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(new Error(`Failed to get from ${store}`));
    });
  }

  async getAll<T>(store: string): Promise<T[]> {
    await this.init();
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([store], 'readonly');
      const objectStore = transaction.objectStore(store);
      const request = objectStore.getAll();

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(new Error(`Failed to get all from ${store}`));
    });
  }

  async getByIndex<T>(store: string, indexName: string, value: any): Promise<T[]> {
    await this.init();
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([store], 'readonly');
      const objectStore = transaction.objectStore(store);
      const index = objectStore.index(indexName);
      const request = index.getAll(value);

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(new Error(`Failed to get by index from ${store}`));
    });
  }

  async put<T>(store: string, data: T): Promise<void> {
    await this.init();
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([store], 'readwrite');
      const objectStore = transaction.objectStore(store);
      const request = objectStore.put(data);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error(`Failed to put to ${store}`));
    });
  }

  async putBatch<T>(store: string, items: T[]): Promise<void> {
    await this.init();
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([store], 'readwrite');
      const objectStore = transaction.objectStore(store);
      
      let completed = 0;
      const total = items.length;

      if (total === 0) {
        resolve();
        return;
      }

      items.forEach((item, index) => {
        const request = objectStore.put(item);
        request.onsuccess = () => {
          completed++;
          if (completed === total) resolve();
        };
        request.onerror = (event) => {
          console.error(`Failed to put item ${index} to ${store}:`, {
            item: item,
            error: request.error,
            event: event
          });
          reject(new Error(`Failed batch put to ${store} at index ${index}: ${request.error?.message || 'Unknown error'}`));
        };
      });
    });
  }

  async delete(store: string, key: string): Promise<void> {
    await this.init();
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([store], 'readwrite');
      const objectStore = transaction.objectStore(store);
      const request = objectStore.delete(key);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error(`Failed to delete from ${store}`));
    });
  }

  async clear(store: string): Promise<void> {
    await this.init();
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([store], 'readwrite');
      const objectStore = transaction.objectStore(store);
      const request = objectStore.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error(`Failed to clear ${store}`));
    });
  }

  // Task-specific operations
  async getTasks(): Promise<Task[]> {
    return this.getAll<Task>(STORES.TASKS);
  }

  async getTasksByShipment(shipmentId: string): Promise<Task[]> {
    return this.getByIndex<Task>(STORES.TASKS, 'shipmentId', shipmentId);
  }

  async getTasksByWorker(workerId: string): Promise<Task[]> {
    return this.getByIndex<Task>(STORES.TASKS, 'assignedTo', workerId);
  }

  async getTasksByStatus(status: string): Promise<Task[]> {
    return this.getByIndex<Task>(STORES.TASKS, 'status', status);
  }

  async putTask(task: Task): Promise<void> {
    // Transform task to ensure proper ObjectId structure
    const transformedTask = this.transformTaskForStorage(task);
    await this.put(STORES.TASKS, transformedTask);
    await this.updateMetadata(STORES.TASKS, Date.now());
  }

  async putTasks(tasks: Task[]): Promise<void> {
    // Transform tasks to ensure proper ObjectId structure
    const transformedTasks = tasks.map(task => this.transformTaskForStorage(task));
    await this.putBatch(STORES.TASKS, transformedTasks);
    await this.updateMetadata(STORES.TASKS, Date.now());
  }

  private transformTaskForStorage(task: any): Task {
    // Debug: Log original task structure
    console.debug('Transforming task for storage:', {
      originalId: task._id,
      originalIdType: typeof task._id,
      taskKeys: Object.keys(task)
    });

    // Ensure _id is properly formatted as ObjectId
    if (task._id && typeof task._id === 'string') {
      task._id = { $oid: task._id };
    }
    
    // Validate that _id exists and has the correct format
    if (!task._id || !task._id.$oid) {
      console.error('Task missing proper _id structure after transformation:', task);
      throw new Error('Task must have _id with $oid property for IndexedDB storage');
    }
    
    // Ensure assignedTo is properly formatted
    if (task.assignedTo && typeof task.assignedTo === 'string') {
      task.assignedTo = { $oid: task.assignedTo };
    }
    
    // Ensure shipmentAssignedTo is properly formatted
    if (task.shipmentAssignedTo && typeof task.shipmentAssignedTo === 'string') {
      task.shipmentAssignedTo = { $oid: task.shipmentAssignedTo };
    }
    
    // Ensure createdBy is properly formatted
    if (task.createdBy && typeof task.createdBy === 'string') {
      task.createdBy = { $oid: task.createdBy };
    }
    
    // Ensure completedBy is properly formatted if it exists
    if (task.completedBy && typeof task.completedBy === 'string') {
      task.completedBy = { $oid: task.completedBy };
    }

    // Ensure dates are proper Date objects or strings
    if (task.createdAt && !(task.createdAt instanceof Date) && typeof task.createdAt === 'object' && task.createdAt.$date) {
      task.createdAt = new Date(task.createdAt.$date);
    } else if (task.createdAt && typeof task.createdAt === 'string') {
      task.createdAt = new Date(task.createdAt);
    }

    if (task.updatedAt && !(task.updatedAt instanceof Date) && typeof task.updatedAt === 'object' && task.updatedAt.$date) {
      task.updatedAt = new Date(task.updatedAt.$date);
    } else if (task.updatedAt && typeof task.updatedAt === 'string') {
      task.updatedAt = new Date(task.updatedAt);
    }

    if (task.completedAt && !(task.completedAt instanceof Date) && typeof task.completedAt === 'object' && task.completedAt.$date) {
      task.completedAt = new Date(task.completedAt.$date);
    } else if (task.completedAt && typeof task.completedAt === 'string') {
      task.completedAt = new Date(task.completedAt);
    }

    if (task.dueDate && !(task.dueDate instanceof Date) && typeof task.dueDate === 'object' && task.dueDate.$date) {
      task.dueDate = new Date(task.dueDate.$date);
    } else if (task.dueDate && typeof task.dueDate === 'string') {
      task.dueDate = new Date(task.dueDate);
    }

    return task as Task;
  }

  // Shipment-specific operations
  async getShipments(): Promise<Shipping[]> {
    return this.getAll<Shipping>(STORES.SHIPMENTS);
  }

  async getShipmentsByClient(clientEmail: string): Promise<Shipping[]> {
    return this.getByIndex<Shipping>(STORES.SHIPMENTS, 'clientEmail', clientEmail);
  }

  async putShipment(shipment: Shipping): Promise<void> {
    // Transform shipment to ensure proper ObjectId structure
    const transformedShipment = this.transformShipmentForStorage(shipment);
    await this.put(STORES.SHIPMENTS, transformedShipment);
    await this.updateMetadata(STORES.SHIPMENTS, Date.now());
  }

  /** Take a deleted shipment out of the cache, so the cache-first Dashboard stops
   *  showing a shipment that no longer exists on the server. */
  async deleteShipment(id: string): Promise<void> {
    await this.delete(STORES.SHIPMENTS, id);
    await this.updateMetadata(STORES.SHIPMENTS, Date.now());
  }

  async putShipments(shipments: Shipping[]): Promise<void> {
    // Transform shipments to ensure proper ObjectId structure
    const transformedShipments = shipments.map(shipment => this.transformShipmentForStorage(shipment));
    await this.putBatch(STORES.SHIPMENTS, transformedShipments);
    await this.updateMetadata(STORES.SHIPMENTS, Date.now());
  }

  private transformShipmentForStorage(shipment: any): Shipping {
    // Debug: Log original shipment structure
    console.debug('Transforming shipment for storage:', {
      originalId: shipment._id,
      originalIdType: typeof shipment._id,
      shipmentKeys: Object.keys(shipment)
    });

    // For shipments, _id should remain as string since keyPath is '_id' not '_id.$oid'
    // Convert ObjectId format back to string if needed
    if (shipment._id && typeof shipment._id === 'object' && shipment._id.$oid) {
      shipment._id = shipment._id.$oid;
    }
    
    // Validate that _id exists and is a string
    if (!shipment._id || typeof shipment._id !== 'string') {
      console.error('Shipment missing proper _id as string after transformation:', shipment);
      throw new Error('Shipment must have _id as string for IndexedDB storage');
    }

    // Ensure dates are proper Date objects or strings
    if (shipment.etd && typeof shipment.etd === 'object' && shipment.etd.$date) {
      shipment.etd = shipment.etd.$date;
    }
    
    if (shipment.eta && typeof shipment.eta === 'object' && shipment.eta.$date) {
      shipment.eta = shipment.eta.$date;
    }

    if (shipment.createdAt && !(shipment.createdAt instanceof Date) && typeof shipment.createdAt === 'object' && shipment.createdAt.$date) {
      shipment.createdAt = new Date(shipment.createdAt.$date);
    } else if (shipment.createdAt && typeof shipment.createdAt === 'string') {
      shipment.createdAt = new Date(shipment.createdAt);
    }

    if (shipment.updatedAt && !(shipment.updatedAt instanceof Date) && typeof shipment.updatedAt === 'object' && shipment.updatedAt.$date) {
      shipment.updatedAt = new Date(shipment.updatedAt.$date);
    } else if (shipment.updatedAt && typeof shipment.updatedAt === 'string') {
      shipment.updatedAt = new Date(shipment.updatedAt);
    }

    return shipment as Shipping;
  }

  // Quote-specific operations
  async getQuotes(): Promise<Quote[]> {
    return this.getAll<Quote>(STORES.QUOTES);
  }

  async getQuoteById(tid: string): Promise<Quote | null> {
    return this.get<Quote>(STORES.QUOTES, tid);
  }

  async getFinalQuotes(): Promise<Quote[]> {
    return this.getByIndex<Quote>(STORES.QUOTES, 'stage', 'final');
  }

  async putQuote(quote: Quote): Promise<void> {
    // Transform quote to ensure proper key structure
    const transformedQuote = this.transformQuoteForStorage(quote);
    await this.put(STORES.QUOTES, transformedQuote);
    await this.updateMetadata(STORES.QUOTES, Date.now());
  }

  async putQuotes(quotes: Quote[]): Promise<void> {
    // Transform quotes to ensure proper key structure
    const transformedQuotes = quotes.map(quote => this.transformQuoteForStorage(quote));
    await this.putBatch(STORES.QUOTES, transformedQuotes);
    await this.updateMetadata(STORES.QUOTES, Date.now());
  }

  private transformQuoteForStorage(quote: any): Quote {
    // Debug: Log original quote structure
    console.debug('Transforming quote for storage:', {
      originalTid: quote.tid,
      originalTidType: typeof quote.tid,
      quoteKeys: Object.keys(quote)
    });

    // For quotes, tid should be a string since keyPath is 'tid'
    if (!quote.tid || typeof quote.tid !== 'string') {
      console.error('Quote missing proper tid as string after transformation:', quote);
      throw new Error('Quote must have tid as string for IndexedDB storage');
    }

    // Handle date transformations for quotes
    if (quote.createdAt && typeof quote.createdAt === 'object' && quote.createdAt.$date) {
      quote.createdAt = new Date(quote.createdAt.$date);
    } else if (quote.createdAt && typeof quote.createdAt === 'string') {
      quote.createdAt = new Date(quote.createdAt);
    }

    if (quote.updatedAt && typeof quote.updatedAt === 'object' && quote.updatedAt.$date) {
      quote.updatedAt = new Date(quote.updatedAt.$date);
    } else if (quote.updatedAt && typeof quote.updatedAt === 'string') {
      quote.updatedAt = new Date(quote.updatedAt);
    }

    return quote as Quote;
  }

  // Contact-specific operations
  async getContacts(): Promise<Contact[]> {
    return this.getAll<Contact>(STORES.CONTACTS);
  }

  async getContactById(id: string): Promise<Contact | null> {
    return this.get<Contact>(STORES.CONTACTS, id);
  }

  async getContactByEmail(email: string): Promise<Contact[]> {
    return this.getByIndex<Contact>(STORES.CONTACTS, 'email', email);
  }

  async putContact(contact: Contact): Promise<void> {
    // Transform contact to ensure proper key structure
    const transformedContact = this.transformContactForStorage(contact);
    await this.put(STORES.CONTACTS, transformedContact);
    await this.updateMetadata(STORES.CONTACTS, Date.now());
  }

  async putContacts(contacts: Contact[]): Promise<void> {
    // Transform contacts to ensure proper key structure  
    const transformedContacts = contacts.map(contact => this.transformContactForStorage(contact));
    await this.putBatch(STORES.CONTACTS, transformedContacts);
    await this.updateMetadata(STORES.CONTACTS, Date.now());
  }

  private transformContactForStorage(contact: any): Contact {
    // Debug: Log original contact structure
    console.debug('Transforming contact for storage:', {
      originalId: contact._id,
      originalIdType: typeof contact._id,
      contactKeys: Object.keys(contact)
    });

    // For contacts, _id should remain as string since keyPath is '_id' not '_id.$oid'
    // Convert ObjectId format back to string if needed
    if (contact._id && typeof contact._id === 'object' && contact._id.$oid) {
      contact._id = contact._id.$oid;
    }
    
    // Validate that _id exists and is a string
    if (!contact._id || typeof contact._id !== 'string') {
      console.error('Contact missing proper _id as string after transformation:', contact);
      throw new Error('Contact must have _id as string for IndexedDB storage');
    }

    // Handle date transformations for contacts
    if (contact.createdAt && typeof contact.createdAt === 'object' && contact.createdAt.$date) {
      contact.createdAt = new Date(contact.createdAt.$date);
    } else if (contact.createdAt && typeof contact.createdAt === 'string') {
      contact.createdAt = new Date(contact.createdAt);
    }

    if (contact.updatedAt && typeof contact.updatedAt === 'object' && contact.updatedAt.$date) {
      contact.updatedAt = new Date(contact.updatedAt.$date);
    } else if (contact.updatedAt && typeof contact.updatedAt === 'string') {
      contact.updatedAt = new Date(contact.updatedAt);
    }

    return contact as Contact;
  }

  // Enhanced quote operations with contact linking
  async getQuotesWithContacts(): Promise<Quote[]> {
    const quotes = await this.getQuotes();
    const contacts = await this.getContacts();
    
    // Create a map of contacts by ID for quick lookup
    const contactMap = new Map<string, Contact>();
    contacts.forEach(contact => {
      if (contact._id) {
        contactMap.set(contact._id, contact);
      }
    });

    // Link contacts to quotes
    return quotes.map(quote => ({
      ...quote,
      contactInfo: quote.contactId ? contactMap.get(quote.contactId) : quote.contactInfo
    }));
  }

  async getFinalQuotesWithContacts(): Promise<Quote[]> {
    const quotes = await this.getFinalQuotes();
    const contacts = await this.getContacts();
    
    // Create a map of contacts by ID for quick lookup
    const contactMap = new Map<string, Contact>();
    contacts.forEach(contact => {
      if (contact._id) {
        contactMap.set(contact._id, contact);
      }
    });

    // Link contacts to quotes
    return quotes.map(quote => ({
      ...quote,
      contactInfo: quote.contactId ? contactMap.get(quote.contactId) : quote.contactInfo
    }));
  }

  // Cache metadata operations
  async updateMetadata(key: string, timestamp: number): Promise<void> {
    const metadata: CacheMetadata = {
      key,
      lastUpdated: timestamp,
      expiresAt: timestamp + (24 * 60 * 60 * 1000), // 24 hours
      version: '1.0'
    };
    await this.put(STORES.METADATA, metadata);
  }

  async getMetadata(key: string): Promise<CacheMetadata | null> {
    return this.get<CacheMetadata>(STORES.METADATA, key);
  }

  async isExpired(key: string): Promise<boolean> {
    const metadata = await this.getMetadata(key);
    if (!metadata) return true;
    return Date.now() > metadata.expiresAt;
  }

  // Sync operations
  async addPendingOperation(operation: Omit<PendingOperation, 'id' | 'timestamp' | 'retries'>): Promise<void> {
    const pendingOp: PendingOperation = {
      ...operation,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      retries: 0
    };
    await this.put(STORES.PENDING_OPERATIONS, pendingOp);
  }

  async getPendingOperations(): Promise<PendingOperation[]> {
    return this.getAll<PendingOperation>(STORES.PENDING_OPERATIONS);
  }

  async removePendingOperation(id: string): Promise<void> {
    await this.delete(STORES.PENDING_OPERATIONS, id);
  }

  async updateSyncStatus(status: Partial<SyncStatus>): Promise<void> {
    const existing = await this.get<SyncStatus>(STORES.SYNC_STATUS, 'main') || {
      lastSync: 0,
      pendingOperations: [],
      conflictResolution: 'server-wins' as const
    };
    await this.put(STORES.SYNC_STATUS, { ...existing, ...status, id: 'main' });
  }

  async getSyncStatus(): Promise<SyncStatus> {
    const status = await this.get<SyncStatus>(STORES.SYNC_STATUS, 'main');
    return status || {
      lastSync: 0,
      pendingOperations: [],
      conflictResolution: 'server-wins'
    };
  }

  // Clear all data
  async clearAllData(): Promise<void> {
    await this.init();
    if (!this.db) throw new Error('Database not initialized');

    const stores = Object.values(STORES);
    for (const store of stores) {
      await this.clear(store);
    }
  }

  // Advanced querying
  async queryTasks(filters: {
    shipmentId?: string;
    status?: string;
    assignedTo?: string;
    stage?: string;
    priority?: string;
  }): Promise<Task[]> {
    const allTasks = await this.getTasks();
    
    return allTasks.filter(task => {
      if (filters.shipmentId && task.shipmentId !== filters.shipmentId) return false;
      if (filters.status && task.status !== filters.status) return false;
      if (filters.assignedTo && task.assignedTo?.$oid !== filters.assignedTo) return false;
      if (filters.stage && task.stage !== filters.stage) return false;
      if (filters.priority && task.priority !== filters.priority) return false;
      return true;
    });
  }

  async getTasksAssignedToWorker(workerId: string): Promise<Task[]> {
    const allTasks = await this.getTasks();
    return allTasks.filter(task => 
      task.assignedTo?.$oid === workerId || 
      task.shipmentAssignedTo?.$oid === workerId
    );
  }

  // Statistics and analytics
  async getTaskStats(): Promise<{
    total: number;
    completed: number;
    pending: number;
    inProgress: number;
    byPriority: Record<string, number>;
    byStage: Record<string, number>;
  }> {
    const tasks = await this.getTasks();
    
    const stats = {
      total: tasks.length,
      completed: 0,
      pending: 0,
      inProgress: 0,
      byPriority: {} as Record<string, number>,
      byStage: {} as Record<string, number>
    };

    tasks.forEach(task => {
      // Status counts
      if (task.status === TaskStatus.COMPLETED) stats.completed++;
      else if (task.status === TaskStatus.IN_PROGRESS) stats.inProgress++;
      else stats.pending++;

      // Priority counts
      stats.byPriority[task.priority] = (stats.byPriority[task.priority] || 0) + 1;
      
      // Stage counts  
      stats.byStage[task.stage] = (stats.byStage[task.stage] || 0) + 1;
    });

    return stats;
  }
}

// Singleton instance
const indexedDBService = new IndexedDBService();

export default indexedDBService;
export { IndexedDBService, STORES };
export type { Quote, Contact, CacheMetadata, SyncStatus, PendingOperation };
